import * as fabric from 'fabric';
import { FormElement, FormElementType, TextInputElement, RadioButtonElement, CheckboxElement, SignatureElement, DigitalSignatureElement, BiometricSignatureElement, InitialsElement, TextElement, AttachmentsListElement, PDFPosition, ScreenPosition } from '../types/form-element.types';
import { CoordinateTransformService, PDFPageViewport } from '../services/coordinate-transform.service';
import { Translations } from '../types/localization.types';

export class FormElementFactory {
  private static coordinateTransform = new CoordinateTransformService();

  /**
   * Create Fabric.js object from FormElement
   * Uses screen coordinates if available, otherwise converts from PDF coordinates
   */
  static createFabricObject(element: FormElement, viewport?: PDFPageViewport, pageDimensions?: { width: number; height: number }, translations?: Translations | null): fabric.Object {
    // Ensure element has screen coordinates calculated
    if (!element.screenPosition && element.pdfPosition && viewport && pageDimensions) {
      element.screenPosition = this.calculateScreenPosition(element.pdfPosition, viewport, pageDimensions);
    }

    // Use viewport scale factor for proper sizing
    const scaleFactor = viewport?.scale || 1.0;
    console.log(`🎨 Creating fabric object for element ${element.id} with scale factor: ${scaleFactor}`);
    console.log(`🎨 Element screen position: ${element.screenPosition?.width}x${element.screenPosition?.height}`);

    let fabricObject: fabric.Object;

    switch ((element as any).type) {
      case FormElementType.TEXT_INPUT:
        fabricObject = this.createTextInputObject(element as TextInputElement, scaleFactor);
        break;
      case FormElementType.RADIO_BUTTON:
        fabricObject = this.createRadioButtonObject(element as RadioButtonElement, scaleFactor);
        break;
      case FormElementType.CHECKBOX:
        fabricObject = this.createCheckboxObject(element as CheckboxElement, scaleFactor);
        break;
      case FormElementType.SIGNATURE:
        fabricObject = this.createSignatureObject(element as SignatureElement, scaleFactor, translations);
        break;
      case FormElementType.DIGITAL_SIGNATURE:
        fabricObject = this.createDigitalSignatureObject(element as DigitalSignatureElement, scaleFactor, translations);
        break;
      case FormElementType.BIOMETRIC_SIGNATURE:
        fabricObject = this.createBiometricSignatureObject(element as BiometricSignatureElement, scaleFactor);
        break;
      case FormElementType.INITIALS:
        fabricObject = this.createInitialsObject(element as InitialsElement, scaleFactor);
        break;
      case FormElementType.TEXT:
        fabricObject = this.createTextObject(element as TextElement, scaleFactor);
        break;
      case FormElementType.ATTACHMENTS_LIST:
        fabricObject = this.createAttachmentsListObject(element as AttachmentsListElement, scaleFactor, translations);
        break;
      default:
        throw new Error(`Unsupported form element type: ${element.type}`);
    }

    return fabricObject;
  }


  /**
   * Calculate screen position from PDF position using viewport
   */
  private static calculateScreenPosition(pdfPosition: PDFPosition, viewport: PDFPageViewport, pageDimensions: { width: number; height: number }): ScreenPosition {
    // Convert PDF rectangle to screen rectangle using the service method
    const screenRect = this.coordinateTransform.pdfRectToScreen(pdfPosition, viewport, pageDimensions);

    return {
      x: screenRect.x,
      y: screenRect.y,
      width: screenRect.width,
      height: screenRect.height,
      angle: pdfPosition.angle
    };
  }

  private static createTextInputObject(element: TextInputElement, scaleFactor: number = 1.0): fabric.Group {
    const { properties } = element;
    // Use screen coordinates if available, fallback to legacy position for backward compatibility
    const position = element.screenPosition || element.position || { x: 0, y: 0, width: 100, height: 32 };

    const rect = new fabric.Rect({
      width: position.width,
      height: position.height,
      fill: properties.backgroundColor || 'white',
      stroke: properties.borderColor || '#ccc',
      strokeWidth: (properties.borderWidth || 1) * scaleFactor,
      rx: 4 * scaleFactor,
      ry: 4 * scaleFactor
    });

    const text = new fabric.Text(properties.defaultValue || properties.placeholder || 'Text Input', {
      left: 8 * scaleFactor,
      top: position.height / 2,
      fontSize: (properties.fontSize || 12) * scaleFactor,
      fontFamily: properties.fontFamily || 'Arial',
      fill: properties.textColor || '#000',
      originY: 'center',
      selectable: false
    });

    const group = new fabric.Group([rect, text], {
      left: position.x,
      top: position.y,
      selectable: true,
      hasControls: true,
      hasBorders: true,
      lockScalingFlip: true,
      moveCursor: 'move',
      hoverCursor: 'move',
      cornerSize: 8 * scaleFactor
    });

    this.setCommonProperties(group, element);
    return group;
  }

  private static createRadioButtonObject(element: RadioButtonElement, scaleFactor: number = 1.0): fabric.Group {
    const { properties } = element;
    const position = element.screenPosition || element.position || { x: 0, y: 0, width: 20, height: 20 };

    const circle = new fabric.Circle({
      radius: 8 * scaleFactor,
      left: 8 * scaleFactor,
      top: position.height / 2,
      originX: 'center',
      originY: 'center',
      fill: 'white',
      stroke: properties.buttonColor || '#666',
      strokeWidth: 2 * scaleFactor
    });

    const innerCircle = new fabric.Circle({
      radius: 4 * scaleFactor,
      left: 8 * scaleFactor,
      top: position.height / 2,
      originX: 'center',
      originY: 'center',
      fill: properties.checked ? (properties.buttonColor || '#007bff') : 'transparent'
    });

    // Create label only if text is provided
    const groupObjects: fabric.Object[] = [circle, innerCircle];

    if (properties.label && properties.label.trim() !== '') {
      const label = new fabric.Text(properties.label, {
        left: 24 * scaleFactor,
        top: position.height / 2,
        fontSize: (properties.fontSize || 14) * scaleFactor,
        fontFamily: properties.fontFamily || 'Arial',
        fill: properties.textColor || '#000',
        originY: 'center',
        selectable: false
      });
      groupObjects.push(label);
    }

    const group = new fabric.Group(groupObjects, {
      left: position.x,
      top: position.y,
      width: position.width,
      height: position.height,
      selectable: true,
      hasControls: true,
      hasBorders: true,
      lockScalingFlip: true,
      moveCursor: 'move',
      hoverCursor: 'move',
      cornerSize: 8 * scaleFactor
    });

    this.setCommonProperties(group, element);
    return group;
  }

  private static createCheckboxObject(element: CheckboxElement, scaleFactor: number = 1.0): fabric.Group {
    const { properties } = element;
    const position = element.screenPosition || element.position || { x: 0, y: 0, width: 20, height: 20 };

    const rect = new fabric.Rect({
      width: 16 * scaleFactor,
      height: 16 * scaleFactor,
      left: 8 * scaleFactor + (8 * scaleFactor), // Center the rectangle
      top: position.height / 2,
      originX: 'center',
      originY: 'center',
      fill: 'white',
      stroke: properties.checkColor || '#666',
      strokeWidth: 2 * scaleFactor,
      rx: 2 * scaleFactor,
      ry: 2 * scaleFactor
    });

    const checkmark = new fabric.Text('✓', {
      left: 8 * scaleFactor + (8 * scaleFactor), // Center within the 16px rectangle
      top: position.height / 2,
      fontSize: 12 * scaleFactor,
      fill: properties.checkColor || '#007bff',
      originX: 'center',
      originY: 'center',
      opacity: properties.checked ? 1 : 0,
      selectable: false
    });

    // Create label only if text is provided
    const groupObjects: fabric.Object[] = [rect, checkmark];

    if (properties.label && properties.label.trim() !== '') {
      const label = new fabric.Text(properties.label, {
        left: 32 * scaleFactor,
        top: position.height / 2,
        fontSize: (properties.fontSize || 14) * scaleFactor,
        fontFamily: properties.fontFamily || 'Arial',
        fill: properties.textColor || '#000',
        originY: 'center',
        selectable: false
      });
      groupObjects.push(label);
    }

    const group = new fabric.Group(groupObjects, {
      left: position.x,
      top: position.y,
      width: position.width,
      height: position.height,
      selectable: true,
      hasControls: true,
      hasBorders: true,
      lockScalingFlip: true,
      moveCursor: 'move',
      hoverCursor: 'move',
      cornerSize: 8 * scaleFactor
    });

    this.setCommonProperties(group, element);
    return group;
  }

  private static createSignatureObject(element: SignatureElement, scaleFactor: number = 1.0, translations?: Translations | null): fabric.Group {
    const { properties } = element;
    const position = element.screenPosition || element.position || { x: 0, y: 0, width: 200, height: 80 };

    const rect = new fabric.Rect({
      width: position.width,
      height: position.height,
      fill: properties.backgroundColor || 'white',
      stroke: properties.borderColor || '#ccc',
      strokeWidth: (properties.borderWidth || 2) * scaleFactor,
      strokeDashArray: [5 * scaleFactor, 5 * scaleFactor],
      rx: 4 * scaleFactor,
      ry: 4 * scaleFactor
    });

    const text = new fabric.Text(translations?.placeholders?.signature || 'Signature', {
      left: position.width / 2,
      top: position.height / 2,
      fontSize: 16 * scaleFactor,
      fontFamily: 'Arial',
      fill: '#999',
      originX: 'center',
      originY: 'center',
      selectable: false
    });

    const group = new fabric.Group([rect, text], {
      left: position.x,
      top: position.y,
      selectable: true,
      hasControls: true,
      hasBorders: true,
      lockScalingFlip: true,
      moveCursor: 'move',
      hoverCursor: 'move',
      cornerSize: 8 * scaleFactor
    });

    this.setCommonProperties(group, element);
    return group;
  }

  private static createDigitalSignatureObject(element: DigitalSignatureElement, scaleFactor: number = 1.0, translations?: Translations | null): fabric.Group {
    const { properties } = element;
    const position = element.screenPosition || element.position || { x: 0, y: 0, width: 200, height: 80 };

    const rect = new fabric.Rect({
      width: position.width,
      height: position.height,
      fill: properties.backgroundColor || 'white',
      stroke: properties.borderColor || '#007bff',
      strokeWidth: (properties.borderWidth || 2) * scaleFactor,
      rx: 4 * scaleFactor,
      ry: 4 * scaleFactor
    });

    const text = new fabric.Text(translations?.placeholders?.digitalSignature || 'Digital Signature', {
      left: position.width / 2,
      top: position.height / 2,
      fontSize: 14 * scaleFactor,
      fontFamily: 'Arial',
      fill: '#333',
      originX: 'center',
      originY: 'center',
      selectable: false
    });



    const group = new fabric.Group([rect, text], {
      left: position.x,
      top: position.y,
      selectable: true,
      hasControls: true,
      hasBorders: true,
      lockScalingFlip: true,
      moveCursor: 'move',
      hoverCursor: 'move',
      cornerSize: 8 * scaleFactor
    });

    this.setCommonProperties(group, element);
    return group;
  }

  private static createBiometricSignatureObject(element: BiometricSignatureElement, scaleFactor: number = 1.0): fabric.Group {
    const { properties } = element;
    const position = element.screenPosition || element.position || { x: 0, y: 0, width: 200, height: 80 };

    const rect = new fabric.Rect({
      width: position.width,
      height: position.height,
      fill: properties.backgroundColor || 'white',
      stroke: properties.borderColor || '#ccc',
      strokeWidth: (properties.borderWidth || 1) * scaleFactor,
      strokeDashArray: [5 * scaleFactor, 5 * scaleFactor],
      rx: 4 * scaleFactor,
      ry: 4 * scaleFactor
    });

    const group = new fabric.Group([rect], {
      left: position.x,
      top: position.y,
      selectable: true,
      hasControls: true,
      hasBorders: true,
      lockScalingFlip: true,
      moveCursor: 'move',
      hoverCursor: 'move',
      cornerSize: 8 * scaleFactor
    });

    this.setCommonProperties(group, element);
    return group;
  }

  private static createInitialsObject(element: InitialsElement, scaleFactor: number = 1.0): fabric.Group {
    const { properties } = element;
    const position = element.screenPosition || element.position || { x: 0, y: 0, width: 150, height: 100 };

    const rect = new fabric.Rect({
      width: position.width,
      height: position.height,
      fill: properties.backgroundColor || 'white',
      stroke: properties.borderColor || '#ccc',
      strokeWidth: (properties.borderWidth || 2) * scaleFactor,
      strokeDashArray: [5 * scaleFactor, 5 * scaleFactor],
      rx: 4 * scaleFactor,
      ry: 4 * scaleFactor
    });

    const tickIcon = new fabric.Text('✓', {
      left: position.width / 2,
      top: position.height / 2,
      fontSize: 32 * scaleFactor,
      fill: '#999',
      originX: 'center',
      originY: 'center',
      selectable: false
    });

    const group = new fabric.Group([rect, tickIcon], {
      left: position.x,
      top: position.y,
      selectable: true,
      hasControls: true,
      hasBorders: true,
      lockScalingFlip: true,
      moveCursor: 'move',
      hoverCursor: 'move',
      cornerSize: 8 * scaleFactor
    });

    this.setCommonProperties(group, element);
    return group;
  }

  private static createTextObject(element: TextElement, scaleFactor: number = 1.0): fabric.IText {
    const { properties } = element;
    const position = element.screenPosition || element.position || { x: 0, y: 0, width: 150, height: 20 };

    const text = new fabric.IText(properties.text || 'Text', {
      left: position.x,
      top: position.y,
      width: position.width,
      fontSize: (properties.fontSize || 14) * scaleFactor,
      fontFamily: properties.fontFamily || 'Arial',
      fontWeight: properties.fontWeight || 'normal',
      fill: properties.textColor || '#000',
      backgroundColor: properties.backgroundColor,
      textAlign: properties.textAlign || 'left',
      fontStyle: properties.italic ? 'italic' : 'normal',
      underline: properties.underline || false,
      angle: properties.angle ? (360 - properties.angle) % 360 : 0, // Apply angle property for rotation (convert counter-clockwise to clockwise for Fabric.js)
      selectable: true,
      hasControls: true,
      hasBorders: true,
      lockScalingFlip: true,
      moveCursor: 'move',
      hoverCursor: 'move',
      cornerSize: 8 * scaleFactor,
      padding:0,
      lineHeight:1,

    });

    this.setCommonProperties(text, element);
    return text;
  }

  private static createAttachmentsListObject(element: AttachmentsListElement, scaleFactor: number = 1.0, translations?: Translations | null): fabric.Group {
    const { properties } = element;
    const position = element.screenPosition || element.position || { x: 0, y: 0, width: 288, height: 108 };

    // Calculate how many attachments can fit based on height and line height
    const fontSize = (properties.fontSize || 10) * scaleFactor;
    const lineHeight = fontSize * 1.5; // Fixed line height multiplier
    const availableHeight = position.height - (20 * scaleFactor); // Reserve space for icon/label
    const attachmentCapacity = Math.max(0, Math.floor(availableHeight / lineHeight));

    // Background rectangle with 25% opacity gray
    const rect = new fabric.Rect({
      width: position.width,
      height: position.height,
      fill: 'rgba(128, 128, 128, 0.25)',
      stroke: '#cccccc',
      strokeWidth: 1 * scaleFactor,
      rx: 4 * scaleFactor,
      ry: 4 * scaleFactor
    });

    // Paperclip icon (using Unicode)
    const icon = new fabric.Text('📎', {
      left: 8 * scaleFactor,
      top: 8 * scaleFactor,
      fontSize: 16 * scaleFactor,
      selectable: false
    });

    // Title text
    const title = new fabric.Text(translations?.placeholders?.attachmentsList || 'Attachments List', {
      left: 32 * scaleFactor,
      top: 8 * scaleFactor,
      fontSize: 12 * scaleFactor,
      fontFamily: 'Arial',
      fontWeight: 'bold',
      fill: '#333',
      selectable: false
    });

    // Capacity preview text
    const capacityLabel = translations?.placeholders?.attachmentsCapacity
      ? translations.placeholders.attachmentsCapacity.replace('{count}', String(attachmentCapacity))
      : `(${attachmentCapacity} attachment${attachmentCapacity !== 1 ? 's' : ''} can fit)`;
    const capacityText = new fabric.Text(
      capacityLabel,
      {
        left: position.width / 2,
        top: position.height - (12 * scaleFactor),
        fontSize: 9 * scaleFactor,
        fontFamily: 'Arial',
        fill: '#999',
        originX: 'center',
        selectable: false
      }
    );

    const group = new fabric.Group([rect, icon, title, capacityText], {
      left: position.x,
      top: position.y,
      selectable: true,
      hasControls: true,
      hasBorders: true,
      lockScalingFlip: true,
      moveCursor: 'move',
      hoverCursor: 'move',
      cornerSize: 8 * scaleFactor
    });

    this.setCommonProperties(group, element);
    return group;
  }

  private static setCommonProperties(fabricObject: fabric.Object, element: FormElement): void {
    const position = element.screenPosition || element.position || element.pdfPosition || { x: 0, y: 0, width: 100, height: 20 };

    // For text elements, prioritize properties.angle over position.angle
    let angleValue = (position as any).angle || 0;
    if (element.type === FormElementType.TEXT && element.properties?.angle !== undefined) {
      angleValue = (360 - element.properties.angle) % 360; // Convert counter-clockwise to clockwise for Fabric.js
    }

    fabricObject.set({
      id: element.id,
      elementType: element.type,
      pageIndex: element.pageIndex,
      angle: angleValue,
      scaleX: (position as any).scaleX || 1,
      scaleY: (position as any).scaleY || 1
    });

    // Store element data for later retrieval
    (fabricObject as any).elementData = element;
  }

  static createFormElement(
    type: FormElementType,
    pdfPosition: PDFPosition,
    pageIndex: number,
    signerId: string,
    properties: any = {},
    existingElements: FormElement[] = []
  ): FormElement {
    const id = this.generateId();
    const name = this.generateDefaultName(type, existingElements);

    const baseElement = {
      id,
      name,
      pageIndex,
      signerId,
      pdfPosition: { ...pdfPosition },
      // Keep legacy position for backward compatibility during migration
      position: { ...pdfPosition },
      properties: { ...properties }
    };

    switch (type) {
      case FormElementType.TEXT_INPUT:
        return {
          ...baseElement,
          type: FormElementType.TEXT_INPUT,
          properties: {
            placeholder: '',
            fontSize: 12,
            fontFamily: 'Arial',
            textColor: '#000',
            backgroundColor: 'white',
            borderColor: '#ccc',
            borderWidth: 1,
            required: false,
            ...properties
          }
        } as TextInputElement;

      case FormElementType.RADIO_BUTTON:
        // Smart default generation for radio buttons
        const radioDefaults = this.generateRadioButtonDefaults(signerId, existingElements);
        return {
          ...baseElement,
          name: radioDefaults.groupName, // Use smart group name
          type: FormElementType.RADIO_BUTTON,
          properties: {
            selectedValue: radioDefaults.selectedValue, // Use smart option value
            label: '', // No default label - can be empty
            checked: false,
            fontSize: 14,
            fontFamily: 'Arial',
            textColor: '#000',
            buttonColor: '#007bff',
            required: false,
            ...properties
          }
        } as RadioButtonElement;

      case FormElementType.CHECKBOX:
        return {
          ...baseElement,
          type: FormElementType.CHECKBOX,
          properties: {
            label: '', // No default label - can be empty
            checked: false,
            fontSize: 14,
            fontFamily: 'Arial',
            textColor: '#000',
            checkColor: '#007bff',
            required: false,
            ...properties
          }
        } as CheckboxElement;

      case FormElementType.SIGNATURE:
        return {
          ...baseElement,
          type: FormElementType.SIGNATURE,
          properties: {
            backgroundColor: 'white',
            borderColor: '#ccc',
            borderWidth: 2,
            required: true,
            ...properties
          }
        } as SignatureElement;

      case FormElementType.DIGITAL_SIGNATURE:
        return {
          ...baseElement,
          type: FormElementType.DIGITAL_SIGNATURE,
          properties: {
            backgroundColor: 'white',
            borderColor: '#007bff',
            borderWidth: 2,
            required: true,
            signatureMethod: 'PKI',
            timestampRequired: false,
            ...properties
          }
        } as DigitalSignatureElement;

      case FormElementType.BIOMETRIC_SIGNATURE:
        return {
          ...baseElement,
          type: FormElementType.BIOMETRIC_SIGNATURE,
          properties: {
            backgroundColor: 'white',
            borderColor: '#28a745',
            borderWidth: 2,
            required: true,
            pressureSensitive: true,
            velocityTracking: false,
            ...properties
          }
        } as BiometricSignatureElement;

      case FormElementType.INITIALS:
        return {
          ...baseElement,
          type: FormElementType.INITIALS,
          properties: {
            backgroundColor: 'white',
            borderColor: '#ccc',
            borderWidth: 2,
            required: true,
            ...properties
          }
        } as InitialsElement;

      case FormElementType.TEXT:
        return {
          ...baseElement,
          type: FormElementType.TEXT,
          properties: {
            text: 'Text',
            fontSize: 14,
            fontFamily: 'Arial',
            fontWeight: 'normal',
            textColor: '#000',
            textAlign: 'left',
            italic: false,
            underline: false,
            angle: 0, // Default angle is zero
            required: false,
            ...properties
          }
        } as TextElement;

      case FormElementType.ATTACHMENTS_LIST:
        return {
          ...baseElement,
          type: FormElementType.ATTACHMENTS_LIST,
          properties: {
            fontSize: 10,
            fontFamily: 'Arial',
            ...properties
          }
        } as AttachmentsListElement;

      default:
        throw new Error(`Unsupported form element type: ${type}`);
    }
  }

  /**
   * Generate smart defaults for radio button group name and selected value
   * - Reuses the last created radio button's group name for the same signer
   * - Generates sequential option values (Option1, Option2, etc.) within the group
   * - Ensures group name is globally unique across ALL elements (not just radio buttons)
   */
  private static generateRadioButtonDefaults(signerId: string, existingElements: FormElement[]): { groupName: string, selectedValue: string } {
    // Filter radio buttons for this signer only
    const signerRadioButtons = existingElements.filter(
      el => el.type === FormElementType.RADIO_BUTTON && el.signerId === signerId
    ) as RadioButtonElement[];

    let groupName: string;

    if (signerRadioButtons.length === 0) {
      // First radio button for this signer - create new group with globally unique name
      groupName = this.generateUniqueGroupName(existingElements);
      return {
        groupName,
        selectedValue: 'Option1'
      };
    }

    // Find the most recently created radio button (last in array)
    const lastRadioButton = signerRadioButtons[signerRadioButtons.length - 1];
    groupName = lastRadioButton.name || this.generateUniqueGroupName(existingElements);

    // Find all radio buttons in the same group for this signer
    const groupRadioButtons = signerRadioButtons.filter(rb => rb.name === groupName);

    // Extract option numbers from existing values (e.g., "Option1" -> 1, "Option2" -> 2)
    const optionNumbers: number[] = [];
    groupRadioButtons.forEach(rb => {
      const match = rb.properties.selectedValue?.match(/^Option(\d+)$/i);
      if (match) {
        optionNumbers.push(parseInt(match[1], 10));
      }
    });

    // Generate next sequential option number
    const nextOptionNumber = optionNumbers.length > 0 ? Math.max(...optionNumbers) + 1 : groupRadioButtons.length + 1;

    return {
      groupName,
      selectedValue: `Option${nextOptionNumber}`
    };
  }

  /**
   * Generate a globally unique radio group name
   * Ensures the name doesn't conflict with ANY existing element name
   */
  private static generateUniqueGroupName(existingElements: FormElement[]): string {
    const existingNames = new Set(existingElements.map(el => el.name).filter(name => name));
    let suffix = 1;
    let groupName = `radio_group_${suffix}`;

    while (existingNames.has(groupName)) {
      suffix++;
      groupName = `radio_group_${suffix}`;
    }

    return groupName;
  }

  private static generateId(): string {
    return `element_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private static generateDefaultName(type: FormElementType, existingElements: FormElement[]): string {
    // Map element types to underscore-separated names
    const typeNameMap: Record<FormElementType, string> = {
      [FormElementType.TEXT_INPUT]: 'text_input',
      [FormElementType.RADIO_BUTTON]: 'radio_group',
      [FormElementType.CHECKBOX]: 'checkbox',
      [FormElementType.SIGNATURE]: 'signature',
      [FormElementType.DIGITAL_SIGNATURE]: 'digital_signature',
      [FormElementType.BIOMETRIC_SIGNATURE]: 'biometric_signature',
      [FormElementType.INITIALS]: 'initials',
      [FormElementType.TEXT]: 'text',
      [FormElementType.ATTACHMENTS_LIST]: 'attachments_list'
    };

    const baseName = typeNameMap[type] || 'element';

    // Get all existing names to ensure global uniqueness
    const existingNames = new Set(existingElements.map(el => el.name).filter(name => name));

    // Count existing elements of the same type to start with a reasonable suffix
    const existingOfSameType = existingElements.filter(el => el.type === type);
    let suffix = existingOfSameType.length + 1;
    let candidateName = `${baseName}_${suffix}`;

    // Increment suffix until we find a globally unique name
    while (existingNames.has(candidateName)) {
      suffix++;
      candidateName = `${baseName}_${suffix}`;
    }

    return candidateName;
  }
}
